/**
 * M3 exploration tuning (plan R18, spec §6.2, §2.9 flows 8-9): `src/data/reach-explore.json`, Zod-validated here.
 * Scanner radii per Mk, chart valuation, cache and beacon rewards, anomaly survey range and hidden-base triggers.
 * Every value is a starting value, tuned in playtest through the "Exploration" dev-panel group.
 */
import { z } from 'zod'
import exploreJson from '../data/reach-explore.json'
import { galaxyGradeIds } from '../data/galaxyConfig'
import { FACTION_KEYS } from '../galaxy/layout/config'
import type { FactionKey } from '../galaxy/layout/types'
import type { RegionId } from '../galaxy/types'
import { PILOT_PROFILES } from '../travel/config'
import type { TuningGroup } from '../render/contracts'
import type { SpawnShipSpec } from '../reach/runtime/types'

const positive = z.number().positive()
const nonNeg = z.number().nonnegative()
/** A whole-number [min, max] range, both ends included. */
const intRange = z
  .tuple([z.number().int().nonnegative(), z.number().int().nonnegative()])
  .refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })
const realRange = z.tuple([nonNeg, nonNeg]).refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })

const shipSpec = z.strictObject({ def: z.string().min(1), profile: z.enum(PILOT_PROFILES), count: z.number().int().positive() })
const groupList = z.array(shipSpec).min(1)
/** Per region; `any` covers regions without their own list. */
const groupTable = z
  .strictObject({ gate: groupList.optional(), mid: groupList.optional(), deep: groupList.optional(), rim: groupList.optional(), any: groupList.optional() })
  .refine((t) => Object.values(t).some((v) => v !== undefined), { message: 'a base side needs at least one group' })

const rewardSchema = z.union([
  z.strictObject({ weight: positive, credits: intRange }),
  z.strictObject({ weight: positive, parts: intRange }),
  z.strictObject({ weight: positive, rumours: intRange }),
])

export const exploreConfigSchema = z.strictObject({
  comment: z.string().optional(),
  scanner: z.strictObject({
    holdS: positive,
    /** Ping radius per scanner Mk (index Mk − 1); null = the whole system. */
    radiusM: z.tuple([positive.nullable(), positive.nullable(), positive.nullable()]),
    cooldownS: nonNeg,
    combatHostileM: positive,
    combatHitS: nonNeg,
  }),
  charts: z.strictObject({
    base: z.strictObject({ gate: positive, mid: positive, deep: positive, rim: positive }),
    prechartedMult: nonNeg,
    firstDiscoveryMult: positive,
    poiRichness: nonNeg,
    hiddenPoiRichness: nonNeg,
    anchorBonus: nonNeg,
    anomalyMult: nonNeg,
    /** System-value factor by the system's richest asteroid grade (poor, standard, rich, pristine; MC-a8). */
    gradeMult: z
      .array(positive)
      .length(galaxyGradeIds.length)
      .refine((m) => m.every((v, i) => i === 0 || v >= m[i - 1]), { message: 'chart grade multipliers must not fall' }),
    /** System-value factor when the system has a mineral signature (MC-a8). */
    signatureMult: positive,
  }),
  /** Holdfast survey knowledge (M8 MC-a10): a credit sink that charts nearby systems without any chart value. */
  survey: z.strictObject({
    /** Credits by the region of the selling station's system. */
    priceByRegion: z.strictObject({ gate: positive, mid: positive, deep: positive, rim: positive }),
    /** Relay hops from the selling station's system that one survey covers. */
    jumps: z.number().int().positive(),
    /** Factions whose stations sell the survey. */
    factions: z.array(z.enum(FACTION_KEYS)).min(1),
  }),
  cache: z.strictObject({ rangeM: positive, maxSpeed: positive, rewards: z.array(rewardSchema).min(1) }),
  beacon: z.strictObject({ rangeM: positive, maxSpeed: positive, extraRumours: intRange, extraHops: z.number().int().nonnegative() }),
  anomaly: z.strictObject({ revealM: positive }),
  hiddenBase: z.strictObject({
    alertM: positive,
    spawnRadiusM: realRange,
    despawnM: positive,
    groups: z.strictObject({ raider: groupTable, guardian: groupTable }),
    rewardCredits: intRange,
    rewardParts: intRange,
  }),
})

export type ExploreConfig = z.infer<typeof exploreConfigSchema>
export type CacheRewardRule = ExploreConfig['cache']['rewards'][number]

/** Parses and validates reach-explore.json (throws a ZodError naming the bad field). */
export function loadExploreConfig(raw: unknown = exploreJson): ExploreConfig {
  return exploreConfigSchema.parse(raw)
}

/** The defender group of a hidden base: the region's own list, a gate base the mid list, else `any`; null when none. */
export function baseGroupFor(cfg: ExploreConfig, side: 'raider' | 'guardian', region: RegionId): readonly SpawnShipSpec[] | null {
  const table = cfg.hiddenBase.groups[side]
  return table[region] ?? (region === 'gate' ? table.mid : undefined) ?? table.any ?? null
}

/** True when `faction`'s stations sell the survey (MC-a10). */
export function sellsSurvey(cfg: ExploreConfig, faction: FactionKey): boolean {
  return cfg.survey.factions.includes(faction)
}

/** The ping radius for scanner `mk` in metres; +Infinity for the whole system. */
export function scanRadiusM(cfg: ExploreConfig, mk: 1 | 2 | 3): number {
  return cfg.scanner.radiusM[mk - 1] ?? Number.POSITIVE_INFINITY
}

/** The "Exploration" dev-panel group over the live config (edits apply in place). */
export function exploreTuningGroup(cfg: ExploreConfig): TuningGroup {
  const s = cfg.scanner
  const c = cfg.charts
  const group: TuningGroup = {
    name: 'Exploration',
    params: {
      scanHoldS: { value: s.holdS, min: 0.5, max: 10, step: 0.1 },
      scanRadiusMk1M: { value: s.radiusM[0] ?? 0, min: 1e6, max: 1e9, step: 1e6 },
      scanRadiusMk2M: { value: s.radiusM[1] ?? 0, min: 1e6, max: 1e10, step: 1e6 },
      scanCooldownS: { value: s.cooldownS, min: 0, max: 60, step: 0.5 },
      combatHostileM: { value: s.combatHostileM, min: 500, max: 50_000, step: 100 },
      combatHitS: { value: s.combatHitS, min: 0, max: 60, step: 1 },
      chartBaseGate: { value: c.base.gate, min: 1, max: 50_000, step: 50 },
      chartBaseMid: { value: c.base.mid, min: 1, max: 50_000, step: 50 },
      chartBaseDeep: { value: c.base.deep, min: 1, max: 50_000, step: 50 },
      chartBaseRim: { value: c.base.rim, min: 1, max: 50_000, step: 50 },
      prechartedMult: { value: c.prechartedMult, min: 0, max: 1, step: 0.01 },
      firstDiscoveryMult: { value: c.firstDiscoveryMult, min: 1, max: 10, step: 0.1 },
      poiRichness: { value: c.poiRichness, min: 0, max: 2, step: 0.01 },
      hiddenPoiRichness: { value: c.hiddenPoiRichness, min: 0, max: 2, step: 0.01 },
      anchorBonus: { value: c.anchorBonus, min: 0, max: 5, step: 0.05 },
      anomalyMult: { value: c.anomalyMult, min: 0, max: 10, step: 0.1 },
      cacheRangeM: { value: cfg.cache.rangeM, min: 50, max: 5000, step: 10 },
      beaconRangeM: { value: cfg.beacon.rangeM, min: 50, max: 5000, step: 10 },
      poiMaxSpeed: { value: cfg.cache.maxSpeed, min: 5, max: 500, step: 5 },
      anomalyRevealM: { value: cfg.anomaly.revealM, min: 500, max: 100_000, step: 100 },
      baseAlertM: { value: cfg.hiddenBase.alertM, min: 1000, max: 100_000, step: 500 },
    },
    apply(): void {
      const n = (k: string, fallback: number): number => {
        const v = group.params[k]?.value
        return typeof v === 'number' && Number.isFinite(v) ? v : fallback
      }
      s.holdS = Math.max(0.1, n('scanHoldS', s.holdS))
      if (s.radiusM[0] !== null) s.radiusM[0] = Math.max(1, n('scanRadiusMk1M', s.radiusM[0]))
      if (s.radiusM[1] !== null) s.radiusM[1] = Math.max(1, n('scanRadiusMk2M', s.radiusM[1]))
      s.cooldownS = Math.max(0, n('scanCooldownS', s.cooldownS))
      s.combatHostileM = Math.max(1, n('combatHostileM', s.combatHostileM))
      s.combatHitS = Math.max(0, n('combatHitS', s.combatHitS))
      c.base.gate = Math.max(1, n('chartBaseGate', c.base.gate))
      c.base.mid = Math.max(1, n('chartBaseMid', c.base.mid))
      c.base.deep = Math.max(1, n('chartBaseDeep', c.base.deep))
      c.base.rim = Math.max(1, n('chartBaseRim', c.base.rim))
      c.prechartedMult = Math.max(0, n('prechartedMult', c.prechartedMult))
      c.firstDiscoveryMult = Math.max(1, n('firstDiscoveryMult', c.firstDiscoveryMult))
      c.poiRichness = Math.max(0, n('poiRichness', c.poiRichness))
      c.hiddenPoiRichness = Math.max(0, n('hiddenPoiRichness', c.hiddenPoiRichness))
      c.anchorBonus = Math.max(0, n('anchorBonus', c.anchorBonus))
      c.anomalyMult = Math.max(0, n('anomalyMult', c.anomalyMult))
      cfg.cache.rangeM = Math.max(1, n('cacheRangeM', cfg.cache.rangeM))
      cfg.beacon.rangeM = Math.max(1, n('beaconRangeM', cfg.beacon.rangeM))
      const maxSpeed = Math.max(1, n('poiMaxSpeed', cfg.cache.maxSpeed))
      cfg.cache.maxSpeed = maxSpeed
      cfg.beacon.maxSpeed = maxSpeed
      cfg.anomaly.revealM = Math.max(1, n('anomalyRevealM', cfg.anomaly.revealM))
      cfg.hiddenBase.alertM = Math.max(1, n('baseAlertM', cfg.hiddenBase.alertM))
    },
  }
  return group
}
